import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
type Language = 'ua' | 'en';
type MobileAppProps = {
  language: Language;
};
const sectionText = {
  ua: {
    title: 'Додаток CHAMALEON',
    description: 'Швидкий доступ до каталогу, матеріалів та новин. Завжди під рукою. Завантажуйте наш додаток для зручної роботи з продукцією.'
  },
  en: {
    title: 'CHAMALEON App',
    description: 'Quick access to catalog, materials, and news. Always at hand. Download our app for convenient work with our products.'
  }
};
export function MobileApp({
  language
}: MobileAppProps) {
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-100px'
  });
  const t = sectionText[language] || sectionText.en;
  return <section ref={ref} className="relative py-20 lg:py-32 bg-chamaleon-black overflow-hidden text-center" data-id="element-163">
      {/* Yellow glow background effect */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] rounded-full opacity-[0.08] pointer-events-none" style={{
      background: 'radial-gradient(circle, #FFB81C 0%, transparent 70%)'
    }} data-id="element-164" />

      <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col items-center" data-id="element-165">
        {/* Title & Underline */}
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.6
      }} className="text-center mb-12 sm:mb-16" data-id="element-166">
          <h2 className="text-3xl sm:text-4xl lg:text-6xl font-bold text-white mb-4" data-id="element-167">
            {t.title}
          </h2>
          <div className="w-20 h-0.5 bg-gradient-to-r from-transparent via-chamaleon-yellow to-transparent mx-auto" data-id="element-168" />
        </motion.div>

        {/* Image Content */}
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.6,
        delay: 0.2
      }} className="relative w-full max-w-3xl mx-auto mb-12" data-id="element-169">
          {/* Decorative background behind image */}
          <div className="absolute inset-0 bg-gradient-to-tr from-chamaleon-yellow/20 to-transparent rounded-3xl blur-2xl transform rotate-2 scale-105" data-id="element-170" />

          <div className="relative rounded-3xl overflow-hidden shadow-2xl border border-white/10 bg-white/5" data-id="element-171">
            <img src="https://cdn.magicpatterns.com/uploads/acU5N9ZrppArWgmrHtEY9h/IMG_5305.jpg" alt="CHAMALEON App Mockup" className="w-full h-auto object-cover max-h-[600px]" data-id="element-172" />
            {/* Subtle overlay to blend with dark theme */}
            <div className="absolute inset-0 bg-black/10 pointer-events-none mix-blend-overlay" data-id="element-173" />
          </div>
        </motion.div>

        {/* Description */}
        <motion.p initial={{
        opacity: 0,
        y: 20
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.6,
        delay: 0.3
      }} className="text-lg sm:text-xl text-gray-400 mb-10 max-w-2xl mx-auto" data-id="element-174">
          {t.description}
        </motion.p>

        {/* Store Buttons */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.6,
        delay: 0.4
      }} className="flex flex-col sm:flex-row items-center justify-center gap-4 w-full" data-id="element-175">
          {/* App Store */}
          <motion.a href="#" whileHover={{
          scale: 1.05
        }} whileTap={{
          scale: 0.95
        }} className="flex items-center gap-3 px-6 py-3 bg-white/5 hover:bg-white/10 border border-white/10 rounded-xl transition-colors min-w-[200px]" data-id="element-176">
            <svg className="w-8 h-8 text-white" viewBox="0 0 24 24" fill="currentColor" data-id="element-177">
              <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z" data-id="element-178" />
            </svg>
            <div className="text-left" data-id="element-179">
              <div className="text-[10px] text-gray-400 leading-none mb-1" data-id="element-180">
                Download on the
              </div>
              <div className="text-lg font-semibold text-white leading-none" data-id="element-181">
                App Store
              </div>
            </div>
          </motion.a>

          {/* Google Play */}
          <motion.a href="#" whileHover={{
          scale: 1.05
        }} whileTap={{
          scale: 0.95
        }} className="flex items-center gap-3 px-6 py-3 bg-white/5 hover:bg-white/10 border border-white/10 rounded-xl transition-colors min-w-[200px]" data-id="element-182">
            <svg className="w-8 h-8" viewBox="0 0 24 24" data-id="element-183">
              <path fill="#EA4335" d="M3.609 1.814L13.792 12 3.61 22.186a.996.996 0 0 1-.61-.92V2.734a1 1 0 0 1 .609-.92z" data-id="element-184" />
              <path fill="#FBBC04" d="M17.556 8.236L14.9 10.893 3.61 1.814l.093-.051 11.96 6.473h1.893z" data-id="element-185" />
              <path fill="#4285F4" d="M17.556 15.764l-2.656-2.657 2.656-2.657 3.444 1.863a1 1 0 0 1 0 1.588l-3.444 1.863z" data-id="element-186" />
              <path fill="#34A853" d="M3.61 22.186L14.9 13.107l2.656 2.657-11.96 6.473-.093-.051h.107z" data-id="element-187" />
            </svg>
            <div className="text-left" data-id="element-188">
              <div className="text-[10px] text-gray-400 leading-none mb-1" data-id="element-189">
                GET IT ON
              </div>
              <div className="text-lg font-semibold text-white leading-none" data-id="element-190">
                Google Play
              </div>
            </div>
          </motion.a>
        </motion.div>
      </div>
    </section>;
}